import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { checkEmail, loginSuccess } from '../sagas/sagaActions';
import { BillingPaymentSection } from '../shared/BillingPaymentSection';
import { ContinueShopping } from '../shared/ContinueShopping';
import styles from '../shared/checkoutLayout.module.scss';
import { ErrorPage } from '../shared/ErrorPage';
import { Header } from '../shared/Header';
import { LegalNotice } from '../shared/LegalNotice';
import { LoadingOverlay } from '../shared/LoadingOverlay';
import { MailStep, MailSummary } from '../shared/Mail';
import { selectHasError } from '../store/appSelectors';
import { setForm } from '../store/billingSlice';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { onTrialLogin } from '../utils/gtm';
import { TrialHero } from './TrialHero';
import { TrialOrderSummary } from './TrialOrderSummary';
import {
  selectTrialInitialized,
  selectTrialLoading,
  selectTrialView,
} from './trialSelectors';

export default function Trial() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const loading = useAppSelector(selectTrialLoading);
  const initialized = useAppSelector(selectTrialInitialized);
  const hasError = useAppSelector(selectHasError);
  const view = useAppSelector(selectTrialView);
  const { form, errors } = useAppSelector((state) => state.billing);
  const { session, countries, localization } = useAppSelector(
    (state) => state.app,
  );
  const { trialCatalog, sourceCatalog } = useAppSelector(
    (state) => state.trial,
  );
  const placing = useAppSelector((state) => state.checkout.placing);
  const { emailValidation, emailValidationError } = useAppSelector(
    (state) => state.checkout,
  );
  const [email, setEmail] = useState('');

  const customerName = (
    session?.Email
      ? `${session.FirstName ?? ''} ${session.LastName ?? ''}`
      : `${form.firstName} ${form.lastName}`
  ).trim();

  useEffect(() => {
    const messageHandler = (e: MessageEvent) => {
      if (e.data === 'closeSignInPopup') {
        dispatch(loginSuccess());
      }
    };

    window.addEventListener('message', messageHandler);

    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, [dispatch]);

  if (hasError) {
    return <ErrorPage />;
  }

  if (!initialized || !view) {
    return (
      <div className={styles.page}>
        <Header />
        <div className={styles.fullPageLoading}>
          <LoadingOverlay />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Header />
      <TrialHero icon={view.icon} title={view.title} subtitle={view.subtitle} />
      <main className={styles.main}>
        {(loading || placing) && <LoadingOverlay />}
        <div className={styles.grid}>
          <div className={styles.left}>
            {form.email ? (
              <>
                <MailSummary
                  email={form.email}
                  name={customerName}
                  isLoggedIn={Boolean(session?.Email)}
                  onEdit={() => {
                    if (!session?.Email) {
                      dispatch(setForm({ email: '' }));
                      return;
                    }

                    window.open(
                      `${window.ACCOUNTS}/logout?return_to=${window.ACCOUNTS}/close&email=${encodeURIComponent(email)}`,
                      'signin',
                      'menubar=0,resizable=1,width=500,height=700,left=300,top=100',
                    );
                  }}
                />
                <BillingPaymentSection
                  total={0}
                  form={form}
                  fieldErrors={errors}
                  countries={countries}
                  submitLabel={t('Start free trial')}
                />
              </>
            ) : (
              <>
                <MailStep
                  email={email}
                  canContinue={['new', 'existing'].includes(
                    emailValidation ?? '',
                  )}
                  error={emailValidationError}
                  onEmailChange={(value) => {
                    setEmail(value);
                    dispatch(checkEmail(value));
                  }}
                  onContinue={() => {
                    if (emailValidation === 'existing') {
                      window.open(
                        `${window.ACCOUNTS}/service_login?return_to=${window.ACCOUNTS}/close&email=${encodeURIComponent(email)}`,
                        'signin',
                        'menubar=0,resizable=1,width=500,height=700,left=300,top=100',
                      );
                    } else {
                      onTrialLogin({
                        trialCatalog,
                        sourceCatalog,
                        localization,
                        email,
                      });

                      dispatch(setForm({ email }));
                    }
                  }}
                />
                <section className={styles.step}>
                  <h2 className={styles.stepTitle}>{t('Payment Details')}</h2>
                </section>
              </>
            )}

            <div className={styles.spacer} />

            <LegalNotice />
          </div>

          <TrialOrderSummary view={view} />
        </div>

        {window.PRICING_PAGE && <ContinueShopping href={window.PRICING_PAGE} />}
      </main>
    </div>
  );
}
